// Sensitivity — tornado sweeps over individual inputs.
//
// For each driver in DRIVERS we run the forecast at base, then at ±sweepPct
// of the driver's nominal value (clamped to [0, 1] for percentages), and
// report the swing in average annual operating profit.
//
// Returns a sorted array (largest swing first) for the tornado chart.

import { runForecast } from './forecast.js';
import { summarize } from './summary.js';

const SWEEP_PCT = 0.3; // ±30%

const DRIVERS = [
  {
    label: 'Avg card spend per active user',
    setLow: (i, lo) => mutScenario(i, 'base', 'freeSpend', i.scenarios.base.freeSpend * lo),
    setHigh: (i, hi) => mutScenario(i, 'base', 'freeSpend', i.scenarios.base.freeSpend * hi),
  },
  {
    label: 'Card activation rate',
    setLow: (i, lo) =>
      mutScenario(
        i,
        'base',
        'cardActivationRate',
        clamp01(i.scenarios.base.cardActivationRate * lo),
      ),
    setHigh: (i, hi) =>
      mutScenario(
        i,
        'base',
        'cardActivationRate',
        clamp01(i.scenarios.base.cardActivationRate * hi),
      ),
  },
  {
    label: 'Pro conversion rate',
    setLow: (i, lo) => mutScenario(i, 'base', 'proConv', i.scenarios.base.proConv * lo),
    setHigh: (i, hi) => mutScenario(i, 'base', 'proConv', i.scenarios.base.proConv * hi),
  },
  {
    label: 'Active rate',
    setLow: (i, lo) =>
      mutScenario(i, 'base', 'activeRate', clamp01(i.scenarios.base.activeRate * lo)),
    setHigh: (i, hi) =>
      mutScenario(i, 'base', 'activeRate', clamp01(i.scenarios.base.activeRate * hi)),
  },
  {
    label: 'Cross-border / FX usage',
    // Engine reads fxUsageRate from regions, not from scenario.xborderPct.
    // Sweep by scaling every region's fxUsageRate uniformly.
    setLow: (i, lo) => mutAllRegions(i, 'fxUsageRate', (v) => clamp01(v * lo)),
    setHigh: (i, hi) => mutAllRegions(i, 'fxUsageRate', (v) => clamp01(v * hi)),
  },
  {
    label: 'Fixed operating cost',
    setLow: (i, lo) => mutTop(i, 'fixedOpexMonthly', i.fixedOpexMonthly * lo),
    setHigh: (i, hi) => mutTop(i, 'fixedOpexMonthly', i.fixedOpexMonthly * hi),
  },
  {
    label: 'Smart contract yield',
    setLow: (i, lo) =>
      mutTop(i, 'stablecoin.smartContractYieldApy', i.stablecoin.smartContractYieldApy * lo),
    setHigh: (i, hi) =>
      mutTop(i, 'stablecoin.smartContractYieldApy', i.stablecoin.smartContractYieldApy * hi),
  },
  {
    label: 'Network cost per wallet operation',
    setLow: (i, lo) =>
      mutTop(
        i,
        'stablecoin.avgNetworkCostPerWalletOperation',
        i.stablecoin.avgNetworkCostPerWalletOperation * lo,
      ),
    setHigh: (i, hi) =>
      mutTop(
        i,
        'stablecoin.avgNetworkCostPerWalletOperation',
        i.stablecoin.avgNetworkCostPerWalletOperation * hi,
      ),
  },
  {
    label: 'Referral channel weight',
    // estimateNewReferrals prefers channelMix.referral.weight over the
    // adoptionRate × avgReferralsPerReferrer heuristic, so the canonical
    // sensitivity driver is the channel weight (the share of net-new from referrals).
    setLow: (i, lo) =>
      mutTop(i, 'channelMix.referral.weight', clamp01((i.channelMix?.referral?.weight ?? 0) * lo)),
    setHigh: (i, hi) =>
      mutTop(i, 'channelMix.referral.weight', clamp01((i.channelMix?.referral?.weight ?? 0) * hi)),
  },
];

function clamp01(x) {
  return Math.max(0, Math.min(1, x));
}

function deepClone(o) {
  return JSON.parse(JSON.stringify(o));
}

function mutScenario(inputs, scenarioKey, field, value) {
  const cloned = deepClone(inputs);
  cloned.scenarios[scenarioKey][field] = value;
  return cloned;
}

function mutTop(inputs, dotPath, value) {
  const cloned = deepClone(inputs);
  const parts = dotPath.split('.');
  let cur = cloned;
  for (let i = 0; i < parts.length - 1; i++) cur = cur[parts[i]];
  cur[parts[parts.length - 1]] = value;
  return cloned;
}

/** Apply `transform` uniformly to `field` across every region in `regions`. */
function mutAllRegions(inputs, field, transform) {
  const cloned = deepClone(inputs);
  for (const k of Object.keys(cloned.regions)) {
    cloned.regions[k][field] = transform(cloned.regions[k][field]);
  }
  return cloned;
}

/**
 * Run the tornado sensitivity sweep for the given inputs.
 * Returns drivers sorted by absolute swing magnitude, descending.
 *
 * @param {object} inputs  validated PulsarInputs
 * @returns {Array<{label, baseY3Cm, lowY3Cm, highY3Cm, swing}>}
 */
export function tornado(inputs) {
  const baseSum = summarize(runForecast(inputs, 'base'));
  const baseY3Cm = baseSum.totalOperatingProfit / 3; // 3-year average operating profit

  const lo = 1 - SWEEP_PCT;
  const hi = 1 + SWEEP_PCT;

  const out = DRIVERS.map((d) => {
    const lowSum = summarize(runForecast(d.setLow(inputs, lo), 'base'));
    const highSum = summarize(runForecast(d.setHigh(inputs, hi), 'base'));
    const lowY3Cm = lowSum.totalOperatingProfit / 3;
    const highY3Cm = highSum.totalOperatingProfit / 3;
    return {
      label: d.label,
      baseY3Cm,
      lowY3Cm,
      highY3Cm,
      swing: Math.abs(highY3Cm - lowY3Cm),
    };
  });

  out.sort((a, b) => b.swing - a.swing);
  return out;
}
